import { MbeClose } from '@/components/icons';
import { CtaLoader, PasswordFeedback, PasswordInput } from '@/components/shared';
import {
  Button,
  Flex,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import type { ISetNewPassword, IBaseForm } from '@/types';
import { useTranslation } from 'react-i18next';
import { minLength, hasUpperLetter, hasDigits, hasSpecialChar } from '@/constants';
import { hasEmptyFields } from '@/utils';
import { PasswordChangeSuccess } from '../PasswordChangeSuccess';

interface ISettingsPasswordModalProps extends IBaseForm<ISetNewPassword> {
  hasChanged: boolean;
  isLoading: boolean;
}

export const SettingsPasswordModal: React.FC<ISettingsPasswordModalProps> = ({
  hasChanged,
  isLoading,
  onSubmit: providedOnSubmit,
}): JSX.Element => {
  const { t } = useTranslation();
  const { isOpen, onClose, onOpen } = useDisclosure();

  const {
    formState: { errors },
    getValues,
    handleSubmit: submitHandler,
    register,
    watch,
  } = useForm<ISetNewPassword>({
    mode: 'all',
    criteriaMode: 'all',
  });

  const areRequiredFieldsEmpty = hasEmptyFields(watch());

  const isDisabled = !!errors.newPassword?.types || areRequiredFieldsEmpty;

  const submitAddressHandler: SubmitHandler<ISetNewPassword> = (data, event): void => {
    event?.preventDefault();
    providedOnSubmit?.(data);
  };

  const ctaButtonContent = isLoading ? <CtaLoader /> : t('ACCOUNT.SETTINGS.CONFIRM');

  return (
    <>
      <ModalOverlay backdropFilter="blur(10px) hue-rotate(90deg)" bg="grey" />
      <ModalContent borderRadius={0} minH="500px" minW={{ base: '50%' }}>
        <ModalBody overflow="hidden" p={0} paddingInlineEnd={0} paddingInlineStart={0}>
          <Flex
            alignItems="center"
            justifyContent="flex-end"
            p={4}
            position="relative"
            w="full"
            zIndex={3}
          >
            <ModalCloseButton
              _hover={{ bgColor: 'white' }}
              bgColor="white"
              borderRadius="full"
              h={10}
              position="absolute"
              right="5%"
              top="15px"
              w={10}
            >
              <MbeClose h="3rem" w="3rem" />
            </ModalCloseButton>
          </Flex>

          <ModalHeader fontSize={32} fontWeight="normal" mb={16} textAlign="center">
            {t('ACCOUNT.SETTINGS.MODIFY_PASSWORD.TITLE')}
          </ModalHeader>
          <Flex
            alignItems="center"
            direction="column"
            justifyContent="center"
            position={{ base: 'absolute' }}
            px={{ base: '5%', md: '0' }}
            w={{ base: 'full' }}
            zIndex={2}
          >
            {hasChanged ? (
              <PasswordChangeSuccess />
            ) : (
              <form
                autoComplete="off"
                id="newPasswordForm"
                name="newPasswordForm"
                style={{
                  display: 'flex',
                  width: '100%',
                }}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onSubmit={submitHandler(submitAddressHandler)}
              >
                <Flex
                  alignItems="center"
                  direction="column"
                  gap={8}
                  justifyContent="space-between"
                  mx={{ base: '5%', md: '25%' }}
                  w="100%"
                >
                  <PasswordInput
                    id="oldPassword"
                    mx="auto"
                    placeholder={t('ACCOUNT.SETTINGS.MODIFY_PASSWORD.INPUT_OLD_PASSWORD')}
                    {...register('oldPassword')}
                  />
                  <PasswordFeedback
                    error={errors.newPassword}
                    isOpen={isOpen}
                    value={getValues('newPassword')}
                    onClose={onClose}
                  >
                    <PasswordInput
                      id="password"
                      mx="auto"
                      placeholder={t('ACCOUNT.SETTINGS.MODIFY_PASSWORD.INPUT_NEW_PASSWORD')}
                      {...register('newPassword', {
                        required: true,
                        validate: {
                          minLength: (value) => minLength(value, 8),
                          hasUpperLetter,
                          hasSpecialChar,
                          hasDigits,
                        },
                        onBlur: onClose,
                        onChange: () => {
                          if (!isOpen) onOpen();
                        },
                      })}
                    />
                  </PasswordFeedback>
                  <Button disabled={isDisabled} type="submit" variant="mbePrimary" w="100%">
                    {ctaButtonContent}
                  </Button>
                </Flex>
              </form>
            )}
          </Flex>
        </ModalBody>
      </ModalContent>
    </>
  );
};
